/**
 * Para ve tarih biçimlemesinin TEK kaynağı.
 *
 * snak'ta bu iş 15 dosyada üç farklı biçimde ve 43 dosyada üç farklı locale ile
 * kopyalanmıştı (tr-TR, fr-BE, en-CA aynı panelde). Bir muhasebe panelinde bu
 * kozmetik bir mesele değildir:
 *
 *   • Gün sınırı: fatura tarihi, KDV dönem sınırı ve "ayın 25'i" vadeleri
 *     Brüksel gününe göre hesaplanır. Tarayıcının saat dilimine bırakılırsa
 *     Türkiye'den (UTC+3) bakan operatör 23:30'da kesilen bir faturayı ertesi
 *     güne ait görür ve dönem yanlış görünür.
 *   • Ondalık ayracı: ekranda Belçika biçimi (virgül) doğru, XML/JSON'da
 *     ÖLÜMCÜL. Intervat'ın `PositiveAmount_Type` deseni `[0-9]+\.[0-9]{2}` —
 *     nokta zorunlu. Bu yüzden iki fonksiyon ayrı isimde: `money` ekran için,
 *     `moneyPlain` makine için. Karıştırılamasın diye asla birbirini çağırmaz.
 */

const TZ = 'Europe/Brussels';

/** Belçika belgesi biçimi: 1.234,56 € */
const MONEY_FMT = new Intl.NumberFormat('fr-BE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const NUMBER_FMT = new Intl.NumberFormat('fr-BE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * ADET biçimi — para DEĞİL.
 *
 * ⚠ `quantity` kolonu `decimal(14,4)`tür ve DTO dört ondalık kabul eder:
 * saat başı çalışılan bir iş 7,3333 saat olabilir. `amount()` iki basamağa
 * KİLİTLİDİR ve 7,3333'ü "7,33" diye gösterirdi — yani dondurulmuş bir belgede
 * satırın gerçek adedi ekranda YANLIŞ görünür, tutarla da tutmazdı. Alt sınır 0:
 * tam sayı adet "3" olarak yazılır, "3,0000" olarak değil.
 */
const QUANTITY_FMT = new Intl.NumberFormat('fr-BE', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 4,
});

const DATE_FMT = new Intl.DateTimeFormat('fr-BE', {
  timeZone: TZ,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const DATETIME_FMT = new Intl.DateTimeFormat('fr-BE', {
  timeZone: TZ,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Sunucu para değerlerini `decimal(14,2)` tutar ve TypeORM bunları STRING
 * olarak döndürür — bu bilinçli, kayan nokta yuvarlaması muhasebede kabul
 * edilemez. Bu yüzden hem string hem number kabul edilir.
 */
function toNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** EKRAN için. Örnek: `1.234,56 €`. Değer yoksa `—` döner, `0,00 €` DEĞİL. */
export function money(value: string | number | null | undefined): string {
  const n = toNumber(value);
  return n === null ? '—' : MONEY_FMT.format(n);
}

/** Para birimi simgesi olmadan, ekran biçiminde: `1.234,56`. */
export function amount(value: string | number | null | undefined): string {
  const n = toNumber(value);
  return n === null ? '—' : NUMBER_FMT.format(n);
}

/** Adet için: en fazla dört ondalık, gereksiz sıfır yok. `7,3333` · `3`. */
export function quantity(value: string | number | null | undefined): string {
  const n = toNumber(value);
  return n === null ? '—' : QUANTITY_FMT.format(n);
}

/**
 * MAKİNE için: nokta ondalıklı, iki basamak, binlik ayracı YOK.
 * XML/CSV/API gövdesine giden her değer bundan geçer. Asla ekranda kullanma.
 */
export function moneyPlain(value: string | number | null | undefined): string {
  const n = toNumber(value);
  return n === null ? '' : n.toFixed(2);
}

/** Yüzde: `21,00 %`. Oranlar sunucuda `decimal(5,2)`. */
export function percent(value: string | number | null | undefined): string {
  const n = toNumber(value);
  return n === null ? '—' : `${NUMBER_FMT.format(n)} %`;
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `02/09/2026` — Brüksel gününe göre. */
export function date(value: string | Date | null | undefined): string {
  const d = toDate(value);
  return d === null ? '—' : DATE_FMT.format(d);
}

/** `02/09/2026 22:05` — Brüksel saatine göre. */
export function dateTime(value: string | Date | null | undefined): string {
  const d = toDate(value);
  return d === null ? '—' : DATETIME_FMT.format(d);
}

/**
 * `2026-09-02` — Brüksel gününe göre ISO tarihi.
 *
 * `toISOString().slice(0,10)` KULLANILMAZ: o UTC gününü verir ve Brüksel'de
 * yaz saatinde 00:00–02:00 arası bir zaman damgası bir ÖNCEKİ güne yazılır.
 * Beyan dönemi sınırında bu doğrudan yanlış döneme kayıt demektir.
 */
const YMD_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export function ymd(value: string | Date | null | undefined): string {
  const d = toDate(value);
  return d === null ? '' : YMD_FMT.format(d);
}

/** Bugünün Brüksel tarihi, `YYYY-MM-DD`. Tarih girişi varsayılanları için. */
export function todayYmd(): string {
  return YMD_FMT.format(new Date());
}
